// Copyright 2020 Google Inc. All rights reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
import * as React from "react";
import { styled } from '@mui/material/styles';
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Url, GAVersion } from "@/constants";
import GeneratedURL from "./GeneratedURL";
import useInputs from "./useInputs";
import ExternalLink from "../../ExternalLink";
import InlineCode from "../../InlineCode";
const PREFIX = 'WebURLBuilder';
const classes = {
    generatedInput: `${PREFIX}-generatedInput`,
    denseTableCell: `${PREFIX}-denseTableCell`,
    buttons: `${PREFIX}-buttons`,
    shortened: `${PREFIX}-shortened`,
    inputs: `${PREFIX}-inputs`,
    bold: `${PREFIX}-bold`,
    share: `${PREFIX}-share`,
    shareInvalid: `${PREFIX}-shareInvalid`
};
const Root = styled('div')(({ theme }) => ({
    [`& .${classes.generatedInput}`]: {
        wordBreak: "break-all",
    },
    [`& .${classes.denseTableCell}`]: {
        whiteSpace: "nowrap",
        "& p": {
            paddingBottom: theme.spacing(0.5),
        },
    },
    [`& .${classes.buttons}`]: {
        display: "flex",
        "& > button": {
            margin: theme.spacing(1),
        },
    },
    [`& .${classes.shortened}`]: {
        marginTop: theme.spacing(1),
        display: "flex",
        alignItems: "center",
        "& > :first-child": {
            flexGrow: 1,
        },
        "& > :not(:first-child)": {
            marginLeft: theme.spacing(1),
        },
    },
    [`& .${classes.inputs}`]: {
        display: "flex",
        flexDirection: "column",
        marginBottom: theme.spacing(1),
        maxWidth: "600px",
    },
    [`& .${classes.bold}`]: {
        fontWeight: "bold",
    },
    [`& .${classes.share}`]: {
        padding: theme.spacing(3),
        display: "flex",
        flexDirection: "column",
    },
    [`& .${classes.shareInvalid}`]: {
        display: "flex",
        flexDirection: "row",
        paddingTop: theme.spacing(3),
        alignItems: "center",
        "& svg": {
            marginRight: theme.spacing(2),
        },
        "& p": {
            paddingBottom: "unset",
        },
    }
}));
const customCampaigns = (React.createElement(ExternalLink, { href: Url.aboutCustomCampaigns }, "Custom Campaigns"));
export const WebURLBuilder = ({ version }) => {
    const { websiteURL, source, setSource, medium, setMedium, campaign, setCampaign, id, setID, term, setTerm, content, setContent, onWebsiteChange, } = useInputs();
    return ((React.createElement(Root, null,
        React.createElement(Typography, { component: 'span', variant: "body1" },
            "This tool allows you to easily add campaign parameters to URLs so you can measure ",
            customCampaigns,
            " in Google Analytics."),
        React.createElement(Typography, { variant: "h3" }, "Enter the website URL and campaign information"),
        React.createElement(Typography, null, "Fill out all fields marked with an asterisk (*), and the campaign URL will be generated for you."),
        React.createElement("section", { className: classes.inputs },
            React.createElement(TextField, { id: "website-url", required: true, value: websiteURL || "", onChange: onWebsiteChange, label: "website URL", size: "small", variant: "outlined", helperText: React.createElement("span", null,
                    "The full website URL (e.g.",
                    " ",
                    React.createElement("span", { className: classes.bold }, "https://www.example.com"),
                    ")") }),
            React.createElement(TextField, { id: "campaign-id", value: id || "", onChange: e => setID(e.target.value), label: "campaign ID", size: "small", variant: "outlined", helperText: React.createElement("span", null, "The ads campaign id.") }),
            React.createElement(TextField, { id: "campaign-source", required: true, value: source || "", onChange: e => setSource(e.target.value), label: "campaign source", size: "small", variant: "outlined", helperText: React.createElement("span", null,
                    "The referrer (e.g. ",
                    React.createElement("span", { className: classes.bold }, "google"),
                    ",",
                    " ",
                    React.createElement("span", { className: classes.bold }, "newsletter"),
                    ")") }),
            React.createElement(TextField, { id: "campaign-medium", required: true, value: medium || "", onChange: e => setMedium(e.target.value), label: "campaign medium", size: "small", variant: "outlined", helperText: React.createElement("span", null,
                    "Marketing medium (e.g. ",
                    React.createElement("span", { className: classes.bold }, "cpc"),
                    ",",
                    " ",
                    React.createElement("span", { className: classes.bold }, "banner"),
                    ",",
                    " ",
                    React.createElement("span", { className: classes.bold }, "email"),
                    ")") }),
            React.createElement(TextField, { id: "campaign-name", required: version === GAVersion.UniversalAnalytics, value: campaign || "", onChange: e => setCampaign(e.target.value), label: "campaign name", size: "small", variant: "outlined", helperText: React.createElement("span", null,
                    "Product, promo code, or slogan (e.g.",
                    " ",
                    React.createElement("span", { className: classes.bold }, "spring_sale"),
                    ") One of campaign name or campaign id are required.") }),
            React.createElement(TextField, { id: "campaign-term", value: term || "", onChange: e => setTerm(e.target.value), label: "campaign term", size: "small", variant: "outlined", helperText: "Identify the paid keywords" }),
            React.createElement(TextField, { id: "campaign-content", value: content || "", onChange: e => setContent(e.target.value), label: "campaign content", size: "small", variant: "outlined", helperText: "Use to differentiate ads" })),
        React.createElement(GeneratedURL, { version: version, source: source || "", websiteURL: websiteURL || "", medium: medium || "", campaign: campaign || "", id: id || "", term: term || "", content: content || "" }),
        React.createElement(Typography, { variant: "h2" }, "More information and examples for each parameter"),
        React.createElement(Typography, { variant: "body1" }, "The following table gives a detailed explanation and example of each of the campaign parameters:"),
        React.createElement(Table, { size: "small" },
            React.createElement(TableHead, null,
                React.createElement(TableRow, null,
                    React.createElement(TableCell, null, "Parameter"),
                    React.createElement(TableCell, null, "Required"),
                    React.createElement(TableCell, null, "Example"),
                    React.createElement(TableCell, null, "Description"))),
            React.createElement(TableBody, null,
                React.createElement(TableRow, null,
                    React.createElement(TableCell, { className: classes.denseTableCell },
                        React.createElement(Typography, { variant: "body1" }, "Campaign ID"),
                        React.createElement(InlineCode, null, "utm_id")),
                    React.createElement(TableCell, null,
                        React.createElement(Typography, { variant: "body1" }, "No")),
                    React.createElement(TableCell, null,
                        React.createElement(Typography, { variant: "body1" },
                            React.createElement(InlineCode, null, "abc.123"))),
                    React.createElement(TableCell, null,
                        React.createElement(Typography, { variant: "body1" },
                            "Used to identify which ads campaign this referral references. Use ",
                            React.createElement(InlineCode, null, "utm_id"),
                            " to identify a specific ads campaign."))),
                React.createElement(TableRow, null,
                    React.createElement(TableCell, { className: classes.denseTableCell },
                        React.createElement(Typography, { variant: "body1" }, "Campaign Source"),
                        React.createElement(InlineCode, null, "utm_source")),
                    React.createElement(TableCell, null,
                        React.createElement(Typography, { variant: "body1" }, "Yes")),
                    React.createElement(TableCell, null,
                        React.createElement(Typography, { variant: "body1" },
                            React.createElement(InlineCode, null, "google"))),
                    React.createElement(TableCell, null,
                        React.createElement(Typography, { variant: "body1" },
                            "Use ",
                            React.createElement(InlineCode, null, "utm_source"),
                            " to identify a search engine, newsletter name, or other source."))),
                React.createElement(TableRow, null,
                    React.createElement(TableCell, { className: classes.denseTableCell },
                        React.createElement(Typography, { variant: "body1" }, "Campaign Medium"),
                        React.createElement(InlineCode, null, "utm_medium")),
                    React.createElement(TableCell, null,
                        React.createElement(Typography, { variant: "body1" }, "Yes")),
                    React.createElement(TableCell, null,
                        React.createElement(Typography, { variant: "body1" },
                            React.createElement(InlineCode, null, "cpc"))),
                    React.createElement(TableCell, null,
                        React.createElement(Typography, { variant: "body1" },
                            "Use ",
                            React.createElement(InlineCode, null, "utm_medium"),
                            " to identify a medium such as email or cost-per-click."))),
                React.createElement(TableRow, null,
                    React.createElement(TableCell, { className: classes.denseTableCell },
                        React.createElement(Typography, { variant: "body1" }, "Campaign Name"),
                        React.createElement(InlineCode, null, "utm_campaign")),
                    React.createElement(TableCell, null,
                        React.createElement(Typography, { variant: "body1" }, "No")),
                    React.createElement(TableCell, null,
                        React.createElement(Typography, { variant: "body1" },
                            React.createElement(InlineCode, null, "spring_sale"))),
                    React.createElement(TableCell, null,
                        React.createElement(Typography, { variant: "body1" },
                            "Used for keyword analysis. Use",
                            " ",
                            React.createElement(InlineCode, null, "utm_campaign"),
                            " to identify a specific product promotion or strategic campaign."))),
                React.createElement(TableRow, null,
                    React.createElement(TableCell, { className: classes.denseTableCell },
                        React.createElement(Typography, { variant: "body1" }, "Campaign Term"),
                        React.createElement(InlineCode, null, "utm_term")),
                    React.createElement(TableCell, null,
                        React.createElement(Typography, { variant: "body1" }, "No")),
                    React.createElement(TableCell, null,
                        React.createElement(Typography, { variant: "body1" },
                            React.createElement(InlineCode, null, "running+shoes"))),
                    React.createElement(TableCell, null,
                        React.createElement(Typography, { variant: "body1" },
                            "Used for paid search. Use ",
                            React.createElement(InlineCode, null, "utm_term"),
                            " to note the keywords for this ad."))),
                React.createElement(TableRow, null,
                    React.createElement(TableCell, { className: classes.denseTableCell },
                        React.createElement(Typography, { variant: "body1" }, "Campaign Content"),
                        React.createElement(InlineCode, null, "utm_content")),
                    React.createElement(TableCell, null,
                        React.createElement(Typography, { variant: "body1" }, "No")),
                    React.createElement(TableCell, null,
                        React.createElement(Typography, { variant: "body1" },
                            React.createElement(InlineCode, null, "logolink"))),
                    React.createElement(TableCell, null,
                        React.createElement(Typography, { variant: "body1" },
                            "Used for A/B testing and content-targeted ads. Use",
                            " ",
                            React.createElement(InlineCode, null, "utm_content"),
                            " to differentiate ads or links that point to the same URL."))))),
        React.createElement(Typography, { variant: "h2" }, "Related Resources"),
        React.createElement(Typography, { variant: "body1", component: "ul" },
            React.createElement("li", null,
                React.createElement(ExternalLink, { href: Url.aboutCampaign }, "About Custom Campaigns")),
            React.createElement("li", null,
                React.createElement(ExternalLink, { href: Url.bestPracticesForCreatingCustomCampaigns }, "Best Practices for creating Custom Campaigns")),
            React.createElement("li", null,
                React.createElement(ExternalLink, { href: Url.aboutReferralTrafficReport }, "About the Refferal Traffic report")),
            React.createElement("li", null,
                React.createElement(ExternalLink, { href: Url.aboutTrafficSourceDimensions }, "About traffic source dimensions")),
            React.createElement("li", null,
                React.createElement(ExternalLink, { href: Url.googleAdsAutoTagging }, "Google Ads Auto-Tagging"))))));
};
export default WebURLBuilder;
